// Dev-only fixture + dispatcher for the person's logins (#466):
// `GET /v1/me/identities` and `DELETE /v1/me/identities/:id`. `null` means "not
// mine" and `mockFetch` falls through.
//
// Stateless, as the mock plane's default: a remove answers 204 and the re-read
// shows the row again — the page's observable outcome (confirm closed, list
// re-read) is what a story asserts, never the fixture's memory. The GitHub
// anchor is the one row that can never be removed, so removing it answers the
// 409 the API refuses the last credential with.
//
// The same family answers the email-proof bind (#7426): no candidate by default,
// one from the shared org under `?mock_link_candidates=1`, and a send that
// answers the 202 — a stateless mock, so the list never changes after it.

import { ApiError } from './api';
import type { IdentitiesPage, Identity } from './identities';
import type { LinkCandidate, LinkCandidatesPage, LinkRequestSent } from './link-candidates';
import { at, me } from './mock-session';

const IDENTITIES: Identity[] = [
  {
    id: 'github',
    provider: 'github',
    accountLogin: me.user.login,
    email: me.email,
    linkedAt: at('2026-01-12'),
    removable: false,
  },
  {
    id: 'idn_acme_oidc',
    provider: 'oidc',
    accountLogin: 'acme-corp',
    email: 'din@acme.example',
    linkedAt: at('2026-09-30'),
    removable: true,
  },
];

const LINK_CANDIDATES: LinkCandidate[] = [
  {
    userId: 'usr_din_acme',
    login: 'din-acme',
    email: me.email,
    provider: 'oidc',
    org: 'acme-corp',
  },
];

export function identitiesMock(path: string, method: string): Promise<unknown> | null {
  const bare = path.split('?')[0] ?? path;
  if (bare === '/v1/me/link-candidates' && method === 'GET') {
    // `?mock_link_candidates=1` only: the common case is a person with no other
    // sign-in on their email, and the default mock is the common case (the
    // layout specs measure the shell without the banner).
    const offered = new URLSearchParams(window.location.search).has('mock_link_candidates');
    return Promise.resolve({ data: offered ? LINK_CANDIDATES : [] } satisfies LinkCandidatesPage);
  }
  if (bare === '/v1/me/link-requests' && method === 'POST') {
    return Promise.resolve({
      sentTo: me.email,
      expiresAt: new Date(Date.now() + 30 * 60_000).toISOString(),
    } satisfies LinkRequestSent);
  }
  if (bare === '/v1/me/identities' && method === 'GET') {
    return Promise.resolve({ data: IDENTITIES } satisfies IdentitiesPage);
  }
  if (bare.startsWith('/v1/me/identities/') && method === 'DELETE') {
    if (bare.endsWith('/github')) {
      return Promise.reject(
        new ApiError(409, `DELETE ${bare} → 409`, undefined, 'Last credential.', {
          code: 'last_credential',
        }),
      );
    }
    return Promise.resolve(undefined);
  }
  return null;
}
